import { useState } from "react"
import { Mail, ExternalLink, Loader2, CheckCircle, X } from "lucide-react"
import { InputCustom, CopyButton } from "../../../components"
import creditAccountsService from "../../../services/creditAccountsService"
import toast from "react-hot-toast"

const PayoneerPayment = ({
  paymentMethods,
  formatCurrency,
  total,
  payoneerEmail,
  setPayoneerEmail,
  payoneerTransactionId,
  setPayoneerTransactionId,
  accountId,
  amountValue,
  currency,
  onSuccess,
  onClose,
  t = (text) => text,
}) => {
  const [isProcessing, setIsProcessing] = useState(false)
  const [isComplete, setIsComplete] = useState(false)
  const [error, setError] = useState(null)

  const handleSubmitPayment = async () => {
    // Validate inputs
    if (!payoneerEmail || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(payoneerEmail)) {
      toast.error(t("Please enter a valid email address"))
      return
    }

    if (!payoneerTransactionId) {
      toast.error(t("Please enter the transaction ID"))
      return
    }

    setIsProcessing(true)
    setError(null)

    try {
      // Prepare payment data
      const paymentData = {
        accountId: accountId,
        amount: amountValue,
        paymentMethod: "payoneer",
        currency: currency,
        payoneerDetails: {
          email: payoneerEmail,
          transactionId: payoneerTransactionId,
        },
      }
      // Make API call to process payment
      const response = await creditAccountsService.processPayoneerPayment(paymentData)

      if (response.data && response.data.statusCode === 200) {
        setIsComplete(true)
        // toast.success(t("Payoneer payment submitted successfully!"))
        // Notify parent component of success
        if (onSuccess) {
          onSuccess()
        }
        // Close modal after showing success

      } else {
        toast.error(response.data?.message || t("Payment processing failed"))
        setError(response.data?.message || t("Payment processing failed"))
      }
    } catch (error) {
      console.error("Payoneer payment error:", error)
      setError(error.response?.data?.message || t("Payment processing failed. Please try again."))
      toast.error(error.response?.data?.message || t("Payment processing failed. Please try again."))
    } finally {
      setIsProcessing(false)
    }
  }

  // Render processing state
  if (isProcessing) {
    return (
      <div className="text-center py-6">
        <div className="w-16 h-16 mx-auto mb-4 flex items-center justify-center">
          <div className="w-12 h-12 rounded-full border-2 border-blue-500 border-t-transparent animate-spin"></div>
        </div>
        <h3 className="text-sm font-medium text-neutral-800 dark:text-neutral-200 mb-2">
          {t("Processing Payoneer Payment")}
        </h3>
        <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-4">
          {t("Please wait while we verify your transaction...")}
        </p>
      </div>
    )
  }

  // Render completion state
  if (isComplete) {
    return (
      <div className="text-center py-6">
        <div className="w-16 h-16 mx-auto mb-4 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center">
          <CheckCircle size={24} className="text-green-500 dark:text-green-400" />
        </div>
        <h3 className="text-sm font-medium text-neutral-800 dark:text-neutral-200 mb-2">
          {t("Payment Submitted Successfully!")}
        </h3>
        <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-4">
          {t("Your Payoneer payment has been submitted. Your account will be credited once the payment is verified.")}
        </p>
      </div>
    )
  }

  // Render error state
  if (error) {
    return (
      <div className="text-center py-6">
        <div className="w-16 h-16 mx-auto mb-4 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center">
          <X size={24} className="text-red-500 dark:text-red-400" />
        </div>
        <h3 className="text-sm font-medium text-neutral-800 dark:text-neutral-200 mb-2">{t("Payment Failed")}</h3>
        <p className="text-xs text-red-500 dark:text-red-400 mb-4">{error}</p>
        <div className="flex flex-col gap-2 justify-center">
          <button
            onClick={() => setError(null)}
            className="px-4 py-2.5 bg-neutral-900 text-white dark:bg-neutral-100 dark:text-black rounded-xl hover:scale-105 transition-all duration-400 text-xs font-medium"
          >
            {t("Try Again")}
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="p-4 bg-neutral-50 dark:border-neutral-900 dark:bg-neutral-950/50 rounded-xl border border-neutral-200 dark:bg-neutral-800">
        <h3 className="text-sm font-medium text-neutral-800 dark:text-neutral-200 mb-3">
          {t("Payoneer Payment Instructions")}
        </h3>
        <p className="text-xs text-neutral-600 dark:text-neutral-400 mb-3">
          {t("Please send")} {formatCurrency(total)} {t("to the following Payoneer account")}:
        </p>
        <div className="space-y-2 mb-4 text-xs">
          <div className="flex justify-between items-center">
            <span className="text-neutral-600 dark:text-neutral-400">{t("Account Name")}:</span>
            <div className="flex items-center gap-1">
              <span className="text-neutral-900 dark:text-neutral-200 font-medium">
                {paymentMethods.payoneer.accountName}
              </span>
              <CopyButton text={paymentMethods.payoneer.accountName} label="Copy account name" />
            </div>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-neutral-600 dark:text-neutral-400">{t("Email")}:</span>
            <div className="flex items-center gap-1">
              <span className="text-neutral-900 dark:text-neutral-200 font-medium">
                {paymentMethods.payoneer.email}
              </span>
              <CopyButton text={paymentMethods.payoneer.email} label="Copy email" />
            </div>
          </div>
        </div>
        <div className="p-2 bg-blue-50 dark:bg-blue-900/20 rounded-xl text-xs text-blue-700 dark:text-blue-300 flex items-start gap-1">
          <ExternalLink size={12} className="mt-0.5 flex-shrink-0" />
          <span>
            <a
              href="https://www.payoneer.com/login/"
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-blue-800 dark:hover:text-blue-200"
            >
              {t("Log in to Payoneer")}
            </a>{" "}
            {t("to make your payment.")}
          </span>
        </div>
      </div>

      <div className="space-y-3">
        <div>
          <InputCustom
            type="email"
            label={t("Your Payoneer Email")}
            placeholder={t("your-email@example.com")}
            value={payoneerEmail}
            onChange={(value) => setPayoneerEmail(value)}
            icon={<Mail size={16} className="text-neutral-500 dark:text-neutral-400" />}
          />
        </div>

        <div>
          <label className="block mb-2 text-xs font-medium text-neutral-800 dark:text-neutral-200">
            {t("Transaction ID")}
          </label>
          <input
            type="text"
            className="w-full px-3 py-2.5 border border-neutral-300 dark:bg-neutral-800 rounded-xl bg-white dark:border-neutral-900 dark:bg-neutral-950 text-neutral-800 dark:text-neutral-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
            placeholder="e.g. PAY-1234567890"
            value={payoneerTransactionId}
            onChange={(e) => setPayoneerTransactionId(e.target.value)}
          />
          <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
            {t("You can find this in your Payoneer transaction history after making the payment")}
          </p>
        </div>

        {/* Submit button */}
        <button
          onClick={handleSubmitPayment}
          disabled={!payoneerEmail || !payoneerTransactionId || isProcessing}
          className="w-full mt-4 px-4 py-2.5 bg-neutral-900 text-white dark:bg-neutral-100 dark:text-black rounded-xl hover:scale-105 transition-all duration-400 text-xs font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
        >
          {isProcessing ? (
            <>
              <Loader2 size={14} className="animate-spin mr-2" />
              {t("Processing...")}
            </>
          ) : (
            t("Submit Payoneer Payment")
          )}
        </button>
      </div>
    </div>
  )
}

export default PayoneerPayment
